import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { MultipleOfferConsentForm } from './MultipleOfferConsentForm';
import { SingleOfferConsentForm } from './SingleOfferConsentForm';
import { offerSelected } from '../../../../app/reducers/offer-selected';
import { preapproved } from '../../../../app/reducers/preapproved';
import { Offer } from '../../../../domain/qualification';
import { YouCanComeBackNote } from '../../../../general/components/footers/you-can-come-back-note';
import { WizardPage } from '../../../../general/components/wizard-page';
import { formatCentsCurrencyWhole } from '../../../../general/utils/formatters';
import { REVIEW_APPLICATION } from '../../../../paths';
import { CreditTermsText } from '../../components/credit-terms-text';
import { OfferCard } from '../../components/offer-card/OfferCard';
import { selectCreditTerms, selectOffer } from '../../reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../reducers/next-step';

const selector = createSelector(
  [selectnextStep, selectCreditTerms],
  ({ loading, action }, { creditTerms, conditionalApproval, offers, selectedOfferId }) => ({
    finalCreditLimit: creditTerms?.finalCreditLimit,
    billingCycle: creditTerms?.billingCycle,
    gracePeriodDays: creditTerms?.gracePeriodDays,
    conditionalApproval,
    loading,
    offers,
    action,
    selectedOfferId,
  }),
);

const hasOfferSummary = (offer?: Offer): offer is Offer & { offerSummaryUrl: string } => !!offer?.offerSummaryUrl;

const PrequalifiedPage: FC = () => {
  const dispatch = useDispatch();
  const {
    finalCreditLimit,
    billingCycle,
    gracePeriodDays,
    conditionalApproval,
    loading,
    offers,
    action,
    selectedOfferId,
  } = useSelector(selector);
  const navigate = useNavigate();

  const handleNext = useCallback(() => {
    if (offers.length === 1) {
      dispatch(offerSelected({ offer: offers[0].offerIndex }));
      dispatch(selectOffer(offers[0].id));
      dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
      return;
    }

    if (action === 'select_offer') {
      dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
      return;
    }

    navigate(REVIEW_APPLICATION);
  }, [dispatch, navigate, offers, action]);

  const handleHighlightOffer = useCallback(
    (offerId: string) => {
      dispatch(selectOffer(offerId));
    },
    [dispatch],
  );

  const handleContinue = useCallback(() => {
    const offer = offers.find((o) => o.id === selectedOfferId);
    if (!offer) return;
    dispatch(offerSelected({ offer: offer.offerIndex }));
    dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
  }, [dispatch, offers, selectedOfferId]);

  const preapprovedFiredRef = useRef(false);
  useEffect(() => {
    if (preapprovedFiredRef.current) return;
    if (finalCreditLimit && billingCycle) {
      preapprovedFiredRef.current = true;
      dispatch(preapproved({ finalCreditLimit, billingCycle, offers }));
    }
  }, [dispatch, finalCreditLimit, billingCycle, offers]);

  const singleOffer = offers[0];

  if (loading) {
    return null;
  }

  if (offers.length > 1) {
    const hideFeeAndRebate = offers.every((o) => o.userFee === offers[0].userFee);
    const showOfferConsent = offers.every(hasOfferSummary);

    return (
      <WizardPage title="Congratulations!" formWidth="lg">
        <Stack spacing={4} alignItems="center" sx={{ pb: 12 }}>
          {/* fit-content wrapper sizes to the cards row (303 × N + 24 × (N−1)).
              The credit-limit box mirrors that width via width: 100%, so its
              right edge aligns with the rightmost card for any offer count. */}
          <Stack spacing={4} sx={{ width: 'fit-content' }}>
            <Stack
              spacing={1}
              alignItems="center"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 2, width: '100%' }}
            >
              <Typography variant="body2" color="textPrimary">
                {"You're prequalified for a credit limit of:"}
              </Typography>
              <Typography variant="h4-bold" component="p" color="primary.main">
                {finalCreditLimit && formatCentsCurrencyWhole(finalCreditLimit)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} useFlexGap alignItems="center">
              {offers.map((offer, index) => (
                <OfferCard
                  key={offer.id}
                  offer={offer}
                  index={index}
                  onSelect={handleHighlightOffer}
                  hideFeeAndRebate={hideFeeAndRebate}
                  clickable
                  selected={offer.id === selectedOfferId}
                />
              ))}
            </Stack>
          </Stack>
          {showOfferConsent && <MultipleOfferConsentForm offers={offers} selectedOfferId={selectedOfferId} />}
          <Typography variant="body2" color="textSecondary" align="center" sx={{ width: '100%', maxWidth: 'md' }}>
            {
              "Except as modified by the offer you're receiving, Coast's standard fees and rewards continue to apply and can be found on our "
            }
            <Link href="https://coastpay.com/fee-schedule/" target="_blank" rel="noopener noreferrer">
              Fee Schedule
            </Link>{' '}
            and{' '}
            <Link href="https://coastpay.com/rewards/" target="_blank" rel="noopener noreferrer">
              rewards page
            </Link>
            {". Fees and rewards are subject to change as detailed in Coast's "}
            <Link href="https://coastpay.com/legal/platform-terms/" target="_blank" rel="noopener noreferrer">
              Platform Agreement
            </Link>{' '}
            and{' '}
            <Link href="https://coastpay.com/legal/card-program-terms/" target="_blank" rel="noopener noreferrer">
              Card Program Terms
            </Link>
            .
          </Typography>
          <YouCanComeBackNote />
        </Stack>
        {!showOfferConsent && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              bgcolor: 'grey.50',
              borderTop: 1,
              borderColor: 'divider',
              py: 2,
              px: 2,
            }}
          >
            <Button
              variant="contained"
              color="coolTonedGradient"
              size="large"
              fullWidth
              disabled={!selectedOfferId}
              onClick={handleContinue}
              sx={{ maxWidth: 375 }}
            >
              Continue with plan
            </Button>
          </Box>
        )}
      </WizardPage>
    );
  }

  return (
    finalCreditLimit && (
      <WizardPage title="Congratulations!" footer={<YouCanComeBackNote />}>
        <Typography variant="body2" align="center" color="textPrimary" mt={2}>
          {"You're prequalified for a credit limit of:"}
        </Typography>
        <Box my={4}>
          <Typography variant="h3-bold" component="p" color="primary.main" align="center">
            {formatCentsCurrencyWhole(finalCreditLimit)}
          </Typography>
        </Box>
        <Stack spacing={2}>
          {conditionalApproval && (
            <Typography variant="body2" align="center" color="textSecondary">
              We may need to verify your revenue prior to approving your application.
            </Typography>
          )}
          <CreditTermsText billingCycle={billingCycle} gracePeriodDays={gracePeriodDays} />
        </Stack>

        {hasOfferSummary(singleOffer) ? (
          <SingleOfferConsentForm offer={singleOffer} />
        ) : (
          <Box mt={3}>
            <Button color="primary" variant="contained" size="large" fullWidth onClick={handleNext}>
              Next
            </Button>
          </Box>
        )}
      </WizardPage>
    )
  );
};

export { PrequalifiedPage };
